import h from 'hyperscript';

import { label } from 'cadenza/utils/aria';
import { assert } from 'cadenza/utils/custom-error';
import { on } from 'cadenza/utils/event-util';
import type { Icon } from 'cadenza/utils/icon/icon';
import { uniqueId } from 'cadenza/utils/unique-id';

import './segmented-control.css';

const COMPONENT_NAME = 'd-segmented-control';
const SEGMENTED_CONTROL_VALUE_CHANGED = 'change';

interface Segment {
  /**
   * The value reported by the component when the user selects the segment.
   */
  value: string;
  /**
   * Label of the segment
   */
  label?: string;
  /**
   * Icon of the segment. If specified, the label will be used as the aria-label value of the icon.
   */
  icon?: Icon;
  /**
   * Whether the segment is disabled
   */
  disabled?: boolean;
}

/**
 * One of ariaLabel and ariaLabelledBy are mandatory.
 */
interface SegmentedControlOptions<T extends string = string> {
  /**
   * Text to be used as aria-label value of the component.
   */
  ariaLabel?: string;
  /**
   * Id of the element used as aria-labelledby value of the component.
   */
  ariaLabelledBy?: string;
  /**
   * Name of the generated input (randomly generated if not provided).
   */
  name?: string;
  /**
   * The initial value
   */
  initialValue?: T;
  /**
   * Direction used to render the elements.
   */
  orientation?: 'horizontal' | 'vertical';
  /**
   * if true, it is possible to have no segment selected by clicking on the selected segment
   */
  deselectable?: false;
}

type Separator = '---';
const SEPARATOR: Separator = '---';

export class SegmentedControl<T extends string = string> extends HTMLElement {

  _initialValue?: T;

  /**
   * @param segments - The segments the user can select between. At least one segment must be provided, otherwise the component will raise an error.
   * @param options - Configuration to customise the render.
   * @param options.ariaLabel
   * @param options.ariaLabelledBy
   * @param options.name
   * @param options.initialValue
   * @param options.orientation
   * @param options.deselectable
   */
  constructor (
    segments: (Segment | Separator)[],
    {
      ariaLabel,
      ariaLabelledBy,
      name = uniqueId(),
      initialValue,
      orientation,
      deselectable = false
    }: SegmentedControlOptions<T>) {
    assert(segments.length >= 1, 'There must be at least one segment');
    super();
    this.classList.add(COMPONENT_NAME);
    this.setAttribute('role', 'radiogroup');
    label(this, { ariaLabel, ariaLabelledBy });

    this._initialValue = initialValue;

    if (orientation === 'vertical') {
      this.classList.add('is-vertical');
    }

    const labelledRadios = segments.flatMap((segment, i) => {
      if (segment === SEPARATOR) {
        return h('div', { attrs: { role: 'separator', 'aria-orientation': orientation === 'horizontal' ? 'horizontal' : 'vertical' } });
      }
      return this._createLabelledRadio(segment, name, i, deselectable);
    });
    this.append(...labelledRadios);
    (this.firstElementChild as HTMLInputElement).checked = true;

    on(this, SEGMENTED_CONTROL_VALUE_CHANGED, event => {
      if (event.target !== this) {
        event.stopImmediatePropagation();
        this.dispatchChange();
      }
    });
  }

  connectedCallback () {
    if (this._initialValue) {
      this.value = this._initialValue;
    }
  }

  dispatchChange () {
    this.dispatchEvent(new CustomEvent(SEGMENTED_CONTROL_VALUE_CHANGED, { detail: { value: this.value }, bubbles: true }));
  }

  // empty string is a valid value here which is used by "attribute-presentation-modification-panel.js"
  set value (value) {
    // uncheck all checked first (without it there is a problem in FF that 'this.querySelector('input:checked')' returns 2 items)
    this.querySelectorAll('input')
      .forEach(item => {
        item.checked = false;
      });

    const input = this.querySelector(`input[value="${value}"]`) as HTMLInputElement;
    if (input) {
      input.checked = true;
    } else {
      const checkedInput = this.querySelector('input:checked') as HTMLInputElement;
      if (checkedInput) {
        checkedInput.checked = false;
      }
    }
  }

  get value () {
    const inputElement = this.querySelector('input:checked') as HTMLInputElement | null;
    if (inputElement) {
      return inputElement.value as T;
    }
    return null;
  }

  set disabled (value) {
    this.querySelectorAll('input').forEach(input => { input.disabled = value; });

    if (value) {
      this.setAttribute('aria-disabled', 'true');
    } else {
      this.removeAttribute('aria-disabled');
    }
  }

  get disabled () {
    return this.getAttribute('aria-disabled') === 'true';
  }

  _createLabelledRadio (
    segment: Segment | string,
    name: string,
    i: number,
    deselectable: boolean): [ HTMLInputElement, HTMLLabelElement ] {
    if (typeof segment === 'string') {
      segment = { value: segment };
    }

    const {
      value,
      label: labelText = value,
      icon,
      disabled = false
    } = segment;

    const input = this._createInput(i, name, value, disabled);

    if (icon) {
      assert(icon.getAttribute('aria-label'), 'Icons must be labelled');
    }

    const labelElement = (icon
      ? h('label', { htmlFor: input.id, title: labelText }, icon)
      : h('label.ellipsis', { htmlFor: input.id }, labelText)) as HTMLLabelElement;

    if (deselectable) {
      this._makeInputDeselectable(input, labelElement);
    }

    return [ input, labelElement ];
  }

  _makeInputDeselectable (input: HTMLInputElement, inputLabel: HTMLLabelElement) {
    // The event listener has to be on the labelElement (not on the input), because there
    // input.checked still has the pre-click value.
    inputLabel.onclick = e => {
      if (input.checked) {
        input.checked = false;
        this.dispatchChange();
        e.preventDefault(); // prevents input onclick event from being fired, which would set checked = true again
      }
    };
    input.onkeydown = e => {
      if (e.code === 'Space') {
        inputLabel.click();
        e.preventDefault(); // In Chrome prevents input onclick event from being fired, which would set checked = true again
      }
    };
    // Prevents onclick event on key up in Firefox
    input.onkeyup = e => {
      if (e.code === 'Space') {
        e.preventDefault();
      }
    };
  }

  _createInput (i: number, name: string, value: string, disabled: boolean): HTMLInputElement {
    return h('input.visuallyhidden', {
      type: 'radio',
      id: `${name}-${i}`,
      name,
      value,
      disabled
    });
  }

}

customElements.define(COMPONENT_NAME, SegmentedControl);
